import {
  CalendarIcon,
  DocumentAddIcon,
  DotsCircleHorizontalIcon,
  DotsVerticalIcon,
  GlobeAltIcon,
  MinusCircleIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  XIcon,
} from '@heroicons/react/solid';
import { Fragment, useState } from 'react';
import Button from '../../components/Button';

import TwitterBirdIcon from '../../components/Icons/twitter';
import MediumIcon from '../../components/Icons/medium';
import TSSimple from '../../components/Icons/ts-simple';
import { useEffect } from 'react';

const { DateTime } = require('luxon');

import InfiniteScroll from 'react-infinite-scroll-component';
import SpinnerIcon from '../Icons/spinner';
import LinkedInLogo from '../Icons/linkedin';

import { useRouter } from 'next/router';
import SlotEditor from '../Modal/SlotEditor';
import QueueDraftSelector from '../NewModals/queue-draft-selector';

import { Dialog, Transition, Menu } from '@headlessui/react';
import { Tooltip } from 'react-tippy';
import { TrashIcon } from '@heroicons/react/outline';
import ButtonTip from '../Tooltip/ButtonTip';
import Schedule from '../Modal/schedule';
import TimezoneButton from './TimezoneButton';
import { client } from '../../utils/http';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Queue({
  passSlotEditor = null,
  setPassSlotEditor = null,
  setRefreshQueue = null,
  refreshQueue,
  userID,
  galleryUsername,
  timezone,
  setTimezone,
  layout,
  slotSelected = null,
  modal = false,
  passRefresh,
  draftSelected = null,
  activeSlotPretty = null,
  activeSlotUnix = null,
  setActiveSlotPretty = null,
  setActiveSlotUnix = null,
}) {
  const router = useRouter();

  const [schedule, setSchedule] = useState([]);
  const [nextStartDate, setNextStartDate] = useState(null);
  const [slotEditorOpen, setSlotEditorOpen] = useState(false);
  const [draftSelectorModalOpen, setDraftSelectorModalOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [activePostID, setActivePostID] = useState(null);
  const [activeUnix, setActiveUnix] = useState(null);
  const [refreshDrafts, setRefreshDrafts] = useState(false);

  const [loading, setLoading] = useState(true);

  const convertTimestampToDayOfWeek = (timestamp) => {
    //get today in the users timezone
    const passedDate = DateTime.fromISO(timestamp, { zone: timezone }).toISO();
    const today = DateTime.now().setZone(timezone).startOf('day').toISO();
    const tomorrow = DateTime.now()
      .setZone(timezone)
      .startOf('day')
      .plus({ days: 1 })
      .toISO();

    //if today is the same as the passed date, return 'today', if passeddate is tomorrow return 'tomorrow', otherwise return the day of the week
    if (passedDate === today) {
      return 'Today';
    } else if (passedDate === tomorrow) {
      return 'Tomorrow';
    } else {
      const dayOfWeek = DateTime.fromISO(passedDate)
        .setZone(timezone)
        .toFormat('EEEE');
      return dayOfWeek;
    }
  };

  const returnNiceDate = (timestamp) => {
    //if year is current year: in format August 12, otherwise in format August 12, YYYY
    const currentYear = DateTime.now().setZone(timezone).toFormat('YYYY');
    const passedYear = DateTime.fromISO(timestamp, { zone: timezone }).toFormat(
      'YYYY',
    );

    if (currentYear === passedYear) {
      return DateTime.fromISO(timestamp, { zone: timezone }).toFormat(
        'MMMM dd',
      );
    } else {
      return DateTime.fromISO(timestamp, { zone: timezone }).toFormat(
        'MMMM dd, YYYY',
      );
    }
  };

  const convertTimeToNiceTime = (hours, minutes) => {
    //input is hours in 24 hour format and minutes
    //output is in format hh:mm am/pm
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour = hours == 12 ? 12 : hours % 12;
    const minute = minutes < 10 ? '0' + minutes : minutes;
    return hour + ':' + minute + '' + ampm;
  };

  useEffect(() => {
    userID && timezone && galleryUsername ? generateQueue() : null;
  }, [userID, timezone, refreshQueue, galleryUsername]);

  async function generateQueue(updateData = false) {
    //!updateData && setLoading(true);

    if (userID && timezone && galleryUsername) {
      !updateData && setRefreshDrafts(true);

      console.log('generateQueue');

      try {
        const { data } = await client.post(`/api/scheduling/queue`, {
          userID: userID,
          slug: galleryUsername,
          timezone: timezone,
          start_date: !updateData
            ? DateTime.now().setZone(timezone).toISO()
            : nextStartDate
            ? nextStartDate
            : DateTime.now().setZone(timezone).toISO(),
        });
        if (!data.success) {
          throw new Error('failed to generate queue');
        }
        setRefreshQueue !== null ? setRefreshQueue(false) : null;
        //add data.schedule to existing schedule state

        const nextStart = data.next_start_date;
        setNextStartDate(nextStart);

        if (updateData) {
          setLoading(false);
          setSchedule([...schedule, ...data.schedule]);
        } else {
          setLoading(false);
          setSchedule(data.schedule);
        }
        return true;
      } catch (err) {
        console.error(err);
        setLoading(false);
        setRefreshQueue !== null ? setRefreshQueue(false) : null;
        return false;
      }
    } else {
      //setLoading(false);
      setRefreshQueue !== null ? setRefreshQueue(false) : null;
      return false;
    }
  }

  const getSlotUnixTime = (slot) => {
    const unix = DateTime.fromISO(slot.date, { zone: timezone }).toMillis();
    //in 2022-08-13 format
    const date = DateTime.fromISO(slot.date, { zone: timezone }).toFormat(
      'YYYY-MM-dd',
    );
    //in 13:22 format
    const time = DateTime.fromISO(slot.date, { zone: timezone }).toFormat(
      'HH:mm',
    );

    //format Today at hh:mm am/pm or tomorrow at hh:mm am/pm or day of week at hh:mm am/pm
    const dayOfWeek = convertTimestampToDayOfWeek(slot.date);
    const timeNice = convertTimeToNiceTime(slot.time.h, slot.time.m);
    const dateNice = returnNiceDate(slot.date);
    const dateTimeNice =
      dayOfWeek === 'Today' || dayOfWeek === 'Tomorrow'
        ? dayOfWeek
        : dateNice + ' at ' + timeNice;

    slotSelected(unix, date, time, dateTimeNice);
  };

  const openSelector = (slot) => {
    const unix = DateTime.fromISO(slot.date, { zone: timezone }).toMillis();
    //in 2022-08-13 format
    const date = DateTime.fromISO(slot.date, { zone: timezone }).toFormat(
      'YYYY-MM-dd',
    );
    //in 13:22 format
    const time = DateTime.fromISO(slot.date, { zone: timezone }).toFormat(
      'HH:mm',
    );

    //format Today at hh:mm am/pm or tomorrow at hh:mm am/pm or day of week at hh:mm am/pm
    const dayOfWeek = convertTimestampToDayOfWeek(slot.date);
    const timeNice = convertTimeToNiceTime(slot.time.h, slot.time.m);
    const dateNice = returnNiceDate(slot.date);
    const dateTimeNice =
      dayOfWeek === 'Today' || dayOfWeek === 'Tomorrow'
        ? dayOfWeek
        : dateNice + ' at ' + timeNice;

    setActiveSlotUnix(unix);
    setActiveSlotPretty(dateTimeNice);

    setDraftSelectorModalOpen(true);
  };

  const openDraft = (id) => {
    router.push({
      pathname: '/editor/[pid]',
      query: { pid: id },
    });
  };

  const refreshSlots = () => {
    passRefresh();
    console.log('refreshSlots');
    setNextStartDate(null);
    generateQueue();
  };

  const draftChoosen = (id) => {
    setDraftSelectorModalOpen(false);
    draftSelected(id);
  };

  const cancelScheduling = (e, item) => {
    e.stopPropagation();
    setActivePostID(item?.id);
    const unix = DateTime.fromISO(item?.date, { zone: timezone }).toMillis();
    setActiveUnix(unix);
    setCancelOpen(true);
  };

  useEffect(() => {
    if (passSlotEditor) {
      setSlotEditorOpen(true);
    }
  }, [passSlotEditor]);

  useEffect(() => {
    if (!slotEditorOpen) {
      setPassSlotEditor !== null ? setPassSlotEditor(false) : null;
    }
  }, [slotEditorOpen]);

  return (
    <>
      <QueueDraftSelector
        refreshDrafts={refreshDrafts}
        setRefreshDrafts={setRefreshDrafts}
        draftSelected={(id) => draftChoosen(id)}
        activeSlotPretty={activeSlotPretty}
        activeSlotUnix={activeSlotUnix}
        userID={userID}
        open={draftSelectorModalOpen}
        setOpen={setDraftSelectorModalOpen}
      />

      <SlotEditor
        refreshSlots={() => refreshSlots()}
        userID={userID}
        isOpen={slotEditorOpen}
        setOpen={setSlotEditorOpen}
      />

      <Schedule
        removeScheduledPost={true}
        getScheduledPosts={() => generateQueue()}
        scheduled={true}
        isOpen={cancelOpen}
        setOpen={setCancelOpen}
        postKey={activePostID}
        passedUnix={activeUnix}
      />

      {loading && !modal ? (
        <div className={`flex w-full flex-col`}>
          <div className="w-16 animate-pulse rounded-full bg-gray-200 p-2" />
          <div className="mt-4 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />

          <div className="mt-6 w-16 animate-pulse rounded-full bg-gray-200 p-2" />
          <div className="mt-4 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />

          <div className="mt-6 w-16 animate-pulse rounded-full bg-gray-200 p-2" />
          <div className="mt-4 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />

          <div className="mt-6 w-16 animate-pulse rounded-full bg-gray-200 p-2" />
          <div className="mt-4 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />

          <div className="mt-6 w-16 animate-pulse rounded-full bg-gray-200 p-2" />
          <div className="mt-4 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
          <div className="mt-2 w-full animate-pulse rounded-lg bg-gray-100 p-4" />
        </div>
      ) : (
        <InfiniteScroll
          dataLength={schedule.length} //This is important field to render the next data
          next={() => generateQueue(true)}
          height={modal ? 300 : undefined}
          width={'100%'}
          hasMore={true}
          loader={
            <div
              className={`flex w-full items-center justify-center px-8 py-4 text-gray-500`}
            >
              <SpinnerIcon className={`h-4 w-4 animate-spin fill-current`} />
            </div>
          }
          endMessage={
            <p style={{ textAlign: 'center' }}>
              <b>Yay! You have seen it all</b>
            </p>
          }
          className={` ${
            layout === 'grid'
              ? 'grid grid-cols-3 gap-8'
              : `flex flex-col ${modal ? 'space-y-4' : 'space-y-8'}`
          } mx-auto w-full items-start justify-start  font-sans antialiased ${
            modal && 'p-4'
          }`}
        >
          {modal && (
            <div className="flex w-full flex-row items-center justify-center space-x-2">
              <Button
                click={() => setSlotEditorOpen(true)}
                prefix={<PencilIcon className="-mr-1 h-4 w-4" />}
                size={'mini'}
                variant={'outline'}
              >
                Edit Slots
              </Button>

              <TimezoneButton timezone={timezone} setTimezone={setTimezone} />
            </div>
          )}
          {schedule.map((day, index) => {
            return (
              <div
                key={index}
                className="flex w-full flex-col items-start justify-start"
              >
                <div className="w-full justify-between">
                  <div
                    className={`flex flex-row items-center justify-start space-x-2 ${
                      modal ? 'text-sm' : 'text-base'
                    }`}
                  >
                    <p className="font-semibold tracking-tight text-gray-900">
                      {convertTimestampToDayOfWeek(day.date)}
                    </p>
                    <p className="text-gray-500">{returnNiceDate(day.date)}</p>
                  </div>
                  <div></div>
                </div>

                <div className={`mt-2 grid w-full grid-cols-1 gap-2`}>
                  {day.items.length > 0 ? (
                    day.items.map((item, index) => {
                      return (
                        <button
                          onClick={() =>
                            modal
                              ? null
                              : item.type === 'draft' && openDraft(item?.id)
                          }
                          key={index}
                          className={`group relative flex flex-col items-start justify-start rounded-lg border border-gray-200/50 text-left text-sm ${
                            item.type === 'draft'
                              ? 'cursor-pointer bg-white p-4 pt-2 shadow-sm shadow-gray-300/10'
                              : item?.best
                              ? 'self-start bg-yellow-500/10 p-2'
                              : 'self-start bg-gray-100 p-2 hover:bg-gray-200/50'
                          }`}
                        >
                          {item.type === 'draft' && (
                            <div className="absolute left-0 top-1/2 h-full w-[1px] -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-0 duration-200 group-hover:opacity-100" />
                          )}

                          <div className="flex w-full flex-row items-center justify-between">
                            <div className="flex flex-row items-center justify-start space-x-2">
                              <p
                                className={`${
                                  item?.best
                                    ? 'text-yellow-600'
                                    : 'text-gray-500'
                                }`}
                              >
                                {convertTimeToNiceTime(
                                  item.time.h,
                                  item.time.m,
                                )}
                              </p>
                              {item?.best && (
                                <div className="flex flex-row items-center justify-center rounded-md bg-yellow-500 px-1.5 py-0.5">
                                  <p className="text-xs font-medium text-white">
                                    High Engagement
                                  </p>
                                </div>
                              )}
                              {item.type === 'draft' && (
                                <div className="h-1 w-1 rounded-full bg-gray-300" />
                              )}
                              {item.type === 'draft' && (
                                <div className="flex flex-row space-x-1">
                                  <TSSimple className="h-3 w-3 fill-current text-gray-500" />
                                  {item.draft.sharedTo?.twitter?.shared && (
                                    <TwitterBirdIcon className="h-3 w-3 fill-current text-gray-500" />
                                  )}
                                  {item.draft.sharedTo?.medium?.shared && (
                                    <MediumIcon className="h-3 w-3 fill-current text-gray-500" />
                                  )}
                                  {item.draft.sharedTo?.linkedin?.shared && (
                                    <LinkedInLogo className="h-3 w-3 fill-current text-gray-500" />
                                  )}
                                </div>
                              )}
                            </div>

                            {item.type === 'draft' ? (
                              <>
                                <ButtonTip
                                  text={'Manage + Unschedule'}
                                  position={'bottom'}
                                >
                                  <button
                                    onClick={(e) => cancelScheduling(e, item)}
                                    className="cursor-pointer rounded-md p-1 text-gray-500 duration-150 hover:bg-gray-200 hover:text-gray-700"
                                  >
                                    <DotsVerticalIcon className="h-4 w-4" />
                                  </button>
                                </ButtonTip>

                                {/*  <Menu onClick={(e) => stopProp(e)} as="div" className="relative inline-block text-left z-50">

                                                        <Menu.Button onClick={(e) => stopProp(e)} className='p-0.5 rounded-md hover:bg-gray-200 text-gray-500 hover:text-gray-700 cursor-pointer duration-150 translate-x-2'>
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                                                            </svg>
                                                        </Menu.Button>


                                                        <Transition
                                                            as={Fragment}
                                                            enter="transition ease-out duration-100"
                                                            enterFrom="transform opacity-0 scale-95"
                                                            enterTo="transform opacity-100 scale-100"
                                                            leave="transition ease-in duration-75"
                                                            leaveFrom="transform opacity-100 scale-100"
                                                            leaveTo="transform opacity-0 scale-95"
                                                        >
                                                            <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                                <div className=" p-1">

                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <a
                                                                                href="#"
                                                                                className={classNames(
                                                                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-900',
                                                                                    'group flex items-center px-2 py-1 text-sm rounded-md font-medium'
                                                                                )}
                                                                            >
                                                                                <XIcon
                                                                                    className="mr-2 h-4 w-4 text-gray-900"
                                                                                    aria-hidden="true"
                                                                                />
                                                                                Unschedule
                                                                            </a>
                                                                        )}
                                                                    </Menu.Item>
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <a
                                                                                href="#"
                                                                                className={classNames(
                                                                                    active ? 'bg-gray-100 text-gray-900' : 'text-red-500',
                                                                                    'group flex items-center px-2 py-1 text-sm rounded-md font-medium'
                                                                                )}
                                                                            >
                                                                                <TrashIcon
                                                                                    className="mr-2 h-4 w-4 text-red-500"
                                                                                    aria-hidden="true"
                                                                                />
                                                                                Delete
                                                                            </a>
                                                                        )}
                                                                    </Menu.Item>
                                                                </div>
                                                            </Menu.Items>
                                                        </Transition>
                                                    </Menu> */}
                              </>
                            ) : modal ? (
                              <button
                                onClick={() => getSlotUnixTime(item)}
                                className="flex flex-row items-center justify-center space-x-1 py-1 text-sm font-medium text-blue-500 duration-150 hover:text-blue-600"
                              >
                                <CalendarIcon className="h-3 w-3 fill-current" />
                                <p>Schedule Here</p>
                              </button>
                            ) : (
                              <div className="flex flex-row space-x-1">
                                {/* <button onClick={() => openSelector(item)} className="opacity-0 space-x-1 group-hover:opacity-100 duration-150 flex py-1 px-2 rounded-md items-center justify-center text-xs text-blue-500 font-medium flex-row hover:bg-blue-500/10 hover:text-blue-600">
                                                            <PlusCircleIcon className="w-3 h-3 fill-current" />
                                                            <p>New Post</p>
                                                        </button> */}
                                <button
                                  onClick={() => openSelector(item)}
                                  className="flex flex-row items-center justify-center space-x-1 rounded-md py-1 px-2 text-xs font-medium text-blue-500 opacity-0 duration-150 hover:bg-blue-500/10 hover:text-blue-600 group-hover:opacity-100"
                                >
                                  <DocumentAddIcon className="h-3 w-3 fill-current" />
                                  <p>Pick Draft</p>
                                </button>
                              </div>
                            )}
                          </div>

                          {item.type === 'draft' ? (
                            <div className={` mt-1 overflow-hidden`}>
                              {item.draft?.content?.title ? (
                                <div className="flex flex-col space-y-1">
                                  <p
                                    className={`font-medium  tracking-tightish text-gray-900`}
                                  >
                                    {item.draft?.content?.title}
                                  </p>
                                </div>
                              ) : (
                                <p
                                  className="whitespace-pre-line text-gray-700 line-clamp-2"
                                  dangerouslySetInnerHTML={{
                                    __html: item.draft?.content?.body,
                                  }}
                                />
                              )}
                            </div>
                          ) : null}

                          <div></div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="flex flex-row items-center justify-start space-x-1">
                      <MinusCircleIcon className="h-3 w-3 fill-current text-gray-500" />
                      <p className="text-sm text-gray-500">
                        You don't have any slots{' '}
                        {convertTimestampToDayOfWeek(day.date)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </InfiniteScroll>
      )}
    </>
  );
}
